import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  ExternalLink,
  RefreshCw,
  Sparkles,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { TableCard } from "@/components/shared/TableCard";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import {
  getTaxCalendarTrackerStatus,
  refreshTaxCalendarTracker,
  type DivergenceReport,
  type SourceReport,
  type TaxCalendarSource,
} from "@/services/taxCalendarTrackerService";

const SOURCE_LABEL: Record<TaxCalendarSource, string> = {
  iva_minimum: "IVA Mínimo",
  dgi_calendar: "DGI — calendario general",
  bps_calendar: "BPS — calendario",
};

/** One line saying what each source actually watches, so the card explains
 *  itself to whoever opens it three months from now. */
const SOURCE_CAPTION: Record<TaxCalendarSource, string> = {
  iva_minimum: "Cuota mensual por tramo (Literal E)",
  dgi_calendar: "Ordinales 2 / 3 / 4 — 36 vencimientos",
  bps_calendar: "PDF anual — monitor por hash",
};

/** Where to look when we have no scrape yet. The live URL comes from the
 *  scrape row itself — DGI publishes a new calendar page every year, so
 *  the 2026 slug below is a starting point, not the truth. */
const SOURCE_FALLBACK_URL: Record<TaxCalendarSource, string> = {
  iva_minimum:
    "https://www.gub.uy/direccion-general-impositiva/comunicacion/publicaciones/cuota-iva-minimo-valores-vigentes",
  dgi_calendar:
    "https://www.gub.uy/direccion-general-impositiva/comunicacion/publicaciones/vencimientos-2026-calendario-general-actualizado",
  bps_calendar: "https://www.bps.gub.uy/849/calendarios-de-vencimientos.html",
};

const MONTH_LABEL: Record<number, string> = {
  1: "ene", 2: "feb", 3: "mar", 4: "abr", 5: "may", 6: "jun",
  7: "jul", 8: "ago", 9: "set", 10: "oct", 11: "nov", 12: "dic",
};

/**
 * The four states a source can be in, in the order that matters when more
 * than one applies: a source read by the AI fallback that ALSO diverges is
 * a divergence first — that's the thing a human has to act on.
 */
type SourceStatus = "failed" | "diverged" | "ai" | "verified";

function statusOf(report: SourceReport): SourceStatus {
  if (!report.ok) return "failed";
  if (report.divergences.length > 0) return "diverged";
  if (report.parsedVia === "ai_fallback") return "ai";
  return "verified";
}

function relativeTs(iso: string | null): string {
  if (!iso) return "sin datos";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return iso;
  const minutes = Math.round((Date.now() - then.getTime()) / 60_000);
  if (minutes < 1) return "recién";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  return days === 1 ? "ayer" : `hace ${days} días`;
}

function absoluteTs(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("es-UY", { dateStyle: "medium", timeStyle: "short" });
}

/**
 * Root-only monitor for the three fiscal-calendar scrapers: what DGI/BPS
 * publish today versus what `fiscal-calendar/*.rules.ts` applies.
 *
 * Each source carries ONE status (see SourceStatus) rendered as a badge —
 * an earlier version put a green check and a red parser error on the same
 * row and left you guessing which was true. Raw parser complaints now live
 * behind "Detalle técnico"; the badge is the answer.
 */
export function TaxCalendarTrackerCard(): JSX.Element {
  const qc = useQueryClient();
  const [showFullGrid, setShowFullGrid] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["platform", "tax-calendar-tracker", "status"],
    queryFn: getTaxCalendarTrackerStatus,
    refetchOnWindowFocus: false,
  });

  const refresh = useMutation({
    mutationFn: refreshTaxCalendarTracker,
    onSuccess: () => {
      toast.success("Scrapers actualizados");
      qc.invalidateQueries({ queryKey: ["platform", "tax-calendar-tracker"] });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "El refresh falló");
    },
  });

  if (isLoading) {
    return (
      <MonitorShell>
        <div className="flex items-center justify-center py-10">
          <LoadingLogo />
        </div>
      </MonitorShell>
    );
  }
  if (isError || !data) {
    return (
      <MonitorShell>
        <p className="px-4 py-6 text-sm text-muted-foreground">
          {error instanceof Error ? error.message : "Error cargando el estado"}
        </p>
      </MonitorShell>
    );
  }

  const { report, hardcoded, referenceYear } = data;

  return (
    <MonitorShell
      year={referenceYear}
      action={
        <Button
          size="sm"
          variant="outline"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
        >
          <RefreshCw
            className={`h-3.5 w-3.5 ${refresh.isPending ? "animate-spin" : ""}`}
          />
          {refresh.isPending ? "Actualizando…" : "Refrescar"}
        </Button>
      }
    >
      <ReportSummary report={report} />

      {/* Hairline separators, not three nested boxes: the card is already a
          surface, and a border inside a border inside a border reads as
          clutter rather than as structure. */}
      <div className="divide-y divide-[var(--fx-s-hairline)]">
        <SourceRow source="iva_minimum" report={report.perSource.iva_minimum}>
          <IvaMinimumTiers
            hardcoded={hardcoded.ivaMinimum}
            referenceYear={referenceYear}
          />
        </SourceRow>

        <SourceRow source="dgi_calendar" report={report.perSource.dgi_calendar}>
          {showFullGrid ? (
            <DgiCalendarGrid
              hardcoded={hardcoded.dgi2026}
              referenceYear={referenceYear}
            />
          ) : (
            <button
              type="button"
              className="rounded-[var(--fx-r-md)] text-xs text-muted-foreground underline underline-offset-2 transition-colors duration-[var(--fx-dur-fast)] ease-[var(--fx-ease)] hover:text-[var(--fx-s-text)]"
              onClick={() => setShowFullGrid(true)}
            >
              Ver los 36 valores codificados
            </button>
          )}
        </SourceRow>

        <SourceRow source="bps_calendar" report={report.perSource.bps_calendar}>
          <p className="text-xs leading-relaxed text-muted-foreground">
            El PDF no se parsea: se vigila su hash y se avisa cuando cambia. La
            grilla por dígito vive en{" "}
            <code className="rounded-[4px] bg-[var(--fx-s-tile)] px-1 py-0.5 font-mono text-[10px]">
              bps.rules.ts
            </code>{" "}
            y se actualiza a mano después de revisar el PDF nuevo.
          </p>
        </SourceRow>
      </div>
    </MonitorShell>
  );
}

function MonitorShell({
  year,
  action,
  children,
}: {
  year?: number;
  action?: React.ReactNode;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold tracking-tight">
          Calendario fiscal — monitor{year ? ` (${year})` : ""}
        </h2>
        {action}
      </div>
      <TableCard>{children}</TableCard>
    </section>
  );
}

/**
 * The one-line verdict. Clean is the common case and should look calm — a
 * green banner every single week trains you to stop reading it, so the
 * quiet state is a plain line and only a real alert gets colour.
 */
function ReportSummary({ report }: { report: DivergenceReport }): JSX.Element {
  const sources = Object.values(report.perSource);
  const aiCount = sources.filter((s) => statusOf(s) === "ai").length;

  if (report.totalAlerts === 0) {
    return (
      <div className="flex items-start gap-2 border-b border-[var(--fx-s-hairline)] px-4 py-3 text-sm">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[var(--fx-accent-700)] dark:text-[var(--fx-accent-300)]" />
        <span>
          Las {sources.length} fuentes coinciden con lo codificado.
          {aiCount > 0 && (
            <span className="text-muted-foreground">
              {" "}
              {aiCount === 1 ? "Una fue leída" : `${aiCount} fueron leídas`} por
              el fallback de IA — ese parser sigue roto.
            </span>
          )}
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 border-b border-[var(--fx-s-hairline)] bg-[rgba(201,94,62,0.06)] px-4 py-3 text-sm">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--fx-warn-text)]" />
      <span>
        <strong>{report.totalAlerts}</strong>{" "}
        {report.totalAlerts === 1 ? "fuente requiere" : "fuentes requieren"}{" "}
        revisión
        {report.totalDivergences > 0 && (
          <>
            {" "}
            ({report.totalDivergences}{" "}
            {report.totalDivergences === 1 ? "celda" : "celdas"} en desacuerdo
            con el código)
          </>
        )}
        . El aviso ya salió a <code>ROOT_NOTIFICATION_EMAIL</code>.
      </span>
    </div>
  );
}

function StatusBadge({ status }: { status: SourceStatus }): JSX.Element {
  switch (status) {
    case "failed":
      return (
        <Badge variant="destructive">
          <AlertTriangle className="h-3 w-3" />
          Sin verificar
        </Badge>
      );
    case "diverged":
      return (
        <Badge variant="destructive">
          <AlertTriangle className="h-3 w-3" />
          En desacuerdo
        </Badge>
      );
    case "ai":
      return (
        <Badge variant="secondary">
          <Sparkles className="h-3 w-3" />
          Verificado por IA
        </Badge>
      );
    case "verified":
      return (
        <Badge variant="default">
          <CheckCircle2 className="h-3 w-3" />
          Verificado
        </Badge>
      );
  }
}

function SourceRow({
  source,
  report,
  children,
}: {
  source: TaxCalendarSource;
  report: SourceReport;
  children: React.ReactNode;
}): JSX.Element {
  const status = statusOf(report);
  return (
    <section className="space-y-3 px-4 py-4">
      <header className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-medium">{SOURCE_LABEL[source]}</h3>
            <StatusBadge status={status} />
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {SOURCE_CAPTION[source]}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3 text-xs text-muted-foreground">
          <span title={absoluteTs(report.scrapedAt)}>
            {relativeTs(report.scrapedAt)}
          </span>
          <a
            href={report.sourceUrl ?? SOURCE_FALLBACK_URL[source]}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-[var(--fx-r-md)] transition-colors duration-[var(--fx-dur-fast)] ease-[var(--fx-ease)] hover:text-[var(--fx-s-text)] hover:underline"
          >
            fuente <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </header>

      {status === "ai" && (
        <p className="text-xs leading-relaxed text-muted-foreground">
          El parser determinístico falló y la IA leyó la página en su lugar. Los
          números de abajo sirven para comparar, pero hay que arreglar el parser.
        </p>
      )}

      {status === "failed" && (
        <p className="text-xs leading-relaxed text-[var(--fx-warn-text)]">
          No pudimos leer la fuente y el fallback de IA tampoco la recuperó: para
          esta fuente el monitor no está comparando nada.
        </p>
      )}

      {report.divergences.length > 0 && (
        <ul className="space-y-1 rounded-[var(--fx-r-md)] bg-[rgba(201,94,62,0.06)] px-3 py-2 text-xs">
          {report.divergences.map((d) => (
            <li
              key={d.label}
              className="flex flex-wrap items-baseline gap-x-2 font-mono"
            >
              <span>{d.label}</span>
              <span className="text-muted-foreground">código {d.hardcoded}</span>
              <span className="text-muted-foreground">·</span>
              <span>
                fuente <strong>{d.scraped}</strong>
              </span>
            </li>
          ))}
        </ul>
      )}

      {children}

      {report.errorMessage && <TechnicalDetail message={report.errorMessage} />}
    </section>
  );
}

/** The raw parser complaint. Real information, but diagnostic detail for
 *  whoever repairs the parser — not the headline, which is the badge. */
function TechnicalDetail({ message }: { message: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="inline-flex items-center gap-1 rounded-[var(--fx-r-md)] text-xs text-muted-foreground transition-colors duration-[var(--fx-dur-fast)] ease-[var(--fx-ease)] hover:text-[var(--fx-s-text)]">
        <ChevronRight
          className={`h-3 w-3 transition-transform duration-[var(--fx-dur-fast)] ease-[var(--fx-ease)] ${open ? "rotate-90" : ""}`}
        />
        Detalle técnico
      </CollapsibleTrigger>
      <CollapsibleContent>
        <p className="mt-2 rounded-[var(--fx-r-md)] bg-[var(--fx-s-tile)] px-3 py-2 font-mono text-[11px] leading-relaxed text-muted-foreground">
          {message}
        </p>
      </CollapsibleContent>
    </Collapsible>
  );
}

function IvaMinimumTiers({
  hardcoded,
  referenceYear,
}: {
  hardcoded: { year: number; full: number; tier50: number; tier25: number } | null;
  referenceYear: number;
}): JSX.Element {
  if (!hardcoded) {
    return (
      <p className="text-xs text-[var(--fx-warn-text)]">
        {referenceYear} todavía no está en la tabla local.
      </p>
    );
  }
  const tiers = [
    { label: "Meses 1-12", note: "25%", value: hardcoded.tier25 },
    { label: "Meses 13-24", note: "50%", value: hardcoded.tier50 },
    { label: "Mes 25+", note: "full", value: hardcoded.full },
  ];
  return (
    <dl className="grid gap-2 sm:grid-cols-3">
      {tiers.map((tier) => (
        <div
          key={tier.label}
          className="rounded-[var(--fx-r-md)] bg-[var(--fx-s-tile)] px-3 py-2"
        >
          <dt className="flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
            <span>{tier.label}</span>
            <span className="text-[10px] uppercase tracking-wide">
              {tier.note}
            </span>
          </dt>
          <dd className="mt-0.5 font-mono text-base tabular-nums">
            ${tier.value.toLocaleString("es-UY")}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function DgiCalendarGrid({
  hardcoded,
  referenceYear,
}: {
  hardcoded: {
    cede: Record<string, number>;
    noCede: Record<string, number>;
    ivaMinimoOrd4: Record<string, number>;
  } | null;
  referenceYear: number;
}): JSX.Element {
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  if (!hardcoded) {
    return (
      <p className="text-xs text-[var(--fx-warn-text)]">
        No hay tablas DGI codificadas para {referenceYear} — hay que agregarlas
        en fiscal-calendar antes de que el monitor pueda comparar.
      </p>
    );
  }
  const rows = [
    { label: "CEDE", days: hardcoded.cede },
    { label: "NO CEDE", days: hardcoded.noCede },
    { label: "Ord. 4", days: hardcoded.ivaMinimoOrd4 },
  ];
  return (
    <div className="space-y-1.5">
      <p className="text-xs text-muted-foreground">
        Días codificados para {referenceYear} — es contra esto que se compara la
        fuente.
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="text-muted-foreground">
              <th className="py-1 pr-3 text-left font-normal">Ordinal</th>
              {months.map((m) => (
                <th key={m} className="px-1.5 py-1 text-right font-normal">
                  {MONTH_LABEL[m]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.label}
                className="border-t border-[var(--fx-s-hairline)]"
              >
                <td className="whitespace-nowrap py-1 pr-3">{row.label}</td>
                {months.map((m) => (
                  <td
                    key={m}
                    className="px-1.5 py-1 text-right font-mono tabular-nums"
                  >
                    {row.days[String(m)] ?? "?"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
